'use client';

import { jsxs, jsx } from "react/jsx-runtime";
import { memo, useEffect } from "react";
import { WarningOctagon, Warning, CheckCircle, Info, X } from "../../../react-icons.js";
import { Button } from "../../atoms/Button/Button.js";
import { Text } from "../../atoms/Text/Text.js";
import { useLiveAnnouncement } from "../../utils/hooks/useLiveAnnouncement.js";
import { classNames } from "../../utils/styles/classNames.js";
import { TOAST_NOTIFICATION_ANIMATION_DURATION } from "./utils/constants.js";
import { useWaitOneFrame } from "./utils/useWaitOneFrame.js";
import { VARIANT_PREFIX_MAP, dismissButtonText } from "./ToastNotification.data.js";
import { defaultProps } from "./ToastNotification.defaultProps.js";
import { iconWrapperBg, iconWrapper, content, body, title, dismissButtonWrapper, hidden, visible, initial, containerBg, container } from "./ToastNotification.css.js";
const VARIANT_ICON_MAP = {
  default: Info,
  success: CheckCircle,
  warning: Warning,
  danger: WarningOctagon
};
const TOAST_NOTIFICATION_ANIMATION_STYLE = {
  transition: `opacity ${TOAST_NOTIFICATION_ANIMATION_DURATION}ms ease-in-out, transform ${TOAST_NOTIFICATION_ANIMATION_DURATION}ms ease-in-out`
};
function ToastNotificationComponent({
  id,
  title: title$1,
  description,
  a11yPrefix: a11yPrefixProp,
  variant: variantProp,
  dismissed,
  onDismiss
}) {
  const variant = variantProp || defaultProps.variant;
  const Icon = VARIANT_ICON_MAP[variant];
  const a11yPrefix = a11yPrefixProp || VARIANT_PREFIX_MAP[variant];
  const mounted = useWaitOneFrame();
  const liveId = `${id}-live`;
  const announce = useLiveAnnouncement();
  const a11yMessage = `${a11yPrefix}: ${title$1}${description ? ` . ${description}` : ""}`;
  useEffect(() => {
    announce(a11yMessage, { id: liveId });
    return () => {
      announce(null, { id: liveId });
    };
  }, [a11yMessage, announce, liveId]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: classNames(
        container,
        containerBg[variant],
        !mounted && initial,
        mounted && !dismissed && visible,
        dismissed && hidden
      ),
      id,
      style: TOAST_NOTIFICATION_ANIMATION_STYLE,
      children: [
        /* @__PURE__ */ jsx("div", { className: classNames(iconWrapper, iconWrapperBg[variant]), children: /* @__PURE__ */ jsx(Icon, {}) }),
        /* @__PURE__ */ jsxs("div", { className: content, children: [
          /* @__PURE__ */ jsxs("div", { className: body, children: [
            /* @__PURE__ */ jsx(
              Text,
              {
                as: "h2",
                className: title,
                color: "onDark.text.primary",
                variant: "label.md",
                children: title$1
              }
            ),
            description && /* @__PURE__ */ jsx(Text, { as: "p", color: "onDark.text.secondary", variant: "body.base", children: description })
          ] }),
          /* @__PURE__ */ jsx("div", { className: dismissButtonWrapper, children: /* @__PURE__ */ jsx(
            Button,
            {
              emphasis: "low",
              icon: X,
              size: "xs",
              hiddenLabel: true,
              onClick: onDismiss,
              onDark: true,
              children: dismissButtonText
            }
          ) })
        ] })
      ]
    }
  );
}
const ToastNotification = /* @__PURE__ */ memo(
  ToastNotificationComponent
);
export {
  ToastNotification
};
